/**
 * A file's Share dialog (same pattern as the page Share dialog), driven by core FileShareViewModel: post it to a
 * conversation with an optional message, copy its link, a public link (workspace policy; expiry; revocable) and who
 * can access it (derived from where it was posted).
 */
import type { FileShareState, FileShareViewModel, MessageDescriptor, ShareTarget } from "@bundle/core";
import { Avatar, Button, Divider, Icon, IconButton, Menu, Pressable, Sheet, Stack, Text, TextField, Toast } from "../../primitives/index.ts";
import { useFormat, useT, useTr } from "../../i18n/I18nProvider.tsx";

export type FileShareDialogProps = {
  open: boolean;
  onClose: () => void;
  vm: FileShareViewModel;
  state: FileShareState;
  fileName: string;
  /** "Who can access" lines (the file page's access panel). */
  access: MessageDescriptor[];
  /** Absolute link to the file page. */
  internalLink: string;
  onCopy: (text: string) => void;
  /** After sharing: open the conversation. */
  onOpenConversation?: (channelId: string) => void;
};

function TargetLine({ target, onPick, selected }: { target: ShareTarget; onPick: () => void; selected?: boolean }) {
  return (
    <Pressable direction="horizontal" radius="md" selected={selected} onPress={onPick} testID={`file-share-target-${target.channelId}`}>
      <Stack grow direction="horizontal" align="center" gap="2.5" paddingX="2" paddingY="1.5">
        {target.isDm ? <Avatar name={target.label} size="avatar.xs" /> : <Icon name={target.kind === "private" ? "lock" : "hash"} size="icon.sm" color="text.secondary" />}
        <Stack grow>
          <Text variant="footnote" lines={1}>
            {target.label}
          </Text>
        </Stack>
      </Stack>
    </Pressable>
  );
}

export function FileShareDialog(p: FileShareDialogProps) {
  const t = useT();
  const tr = useTr();
  const fmt = useFormat();
  const { vm, state: s } = p;
  const expiry = s.expiryOptions.find((o) => o.key === s.linkExpiry) ?? s.expiryOptions[0]!;
  return (
    <Sheet open={p.open} onClose={p.onClose} title={t("files.share.title")} closeLabel={t("common.close")} testID="file-share-dialog">
      <Stack gap="5">
        <Text weight="semibold" lines={2}>
          {p.fileName}
        </Text>

        {/* Post to a conversation */}
        <Stack gap="2" testID="file-share-post">
          <Text variant="footnote" weight="semibold" color="text.secondary">
            {t("files.share.to")}
          </Text>
          {!s.canShare && s.status === "ready" ? (
            <Text variant="footnote" color="text.secondary" testID="file-share-no-download">
              {t("files.share.noDownload")}
            </Text>
          ) : s.target !== undefined ? (
            <Stack gap="2">
              <Stack direction="horizontal" align="center" gap="2">
                <Stack grow>
                  <TargetLine target={s.target} selected onPick={() => vm.choose(undefined)} />
                </Stack>
                <IconButton icon="close" size="sm" accessibilityLabel={t("common.cancel")} onPress={() => vm.choose(undefined)} testID="file-share-clear-target" />
              </Stack>
              <TextField value={s.message} onChange={(m) => vm.setMessage(m)} placeholder={t("files.share.message")} multiline testID="file-share-message" />
              <Stack direction="horizontal" justify="end">
                <Button
                  label={t("files.share.send")}
                  icon="send"
                  loading={s.sending}
                  onPress={() =>
                    void vm.send().then((channelId) => {
                      if (channelId !== undefined) p.onOpenConversation?.(channelId);
                    })
                  }
                  testID="file-share-send"
                />
              </Stack>
            </Stack>
          ) : (
            <Stack gap="1">
              <TextField value={s.query} onChange={(q) => vm.setQuery(q)} placeholder={t("files.share.pick")} leadingIcon="search" testID="file-share-search" />
              <Stack gap="0.5" testID="file-share-targets">
                {s.targets.map((target) => (
                  <TargetLine key={target.channelId} target={target} onPick={() => vm.choose(target)} />
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>

        <Divider />

        {/* Public link */}
        <Stack gap="2" testID="file-share-public">
          <Stack direction="horizontal" align="center" gap="3">
            <Stack padding="1.5" radius="md" background={s.link === undefined ? "bg.surface" : "accent.subtle"}>
              <Icon name="globe" size="icon.sm" color={s.link === undefined ? "text.secondary" : "accent.text"} />
            </Stack>
            <Stack grow gap="0.5">
              <Text weight="medium">{t("files.share.publicLink")}</Text>
              <Text variant="caption" color="text.secondary">
                {s.linkBlockedReason === undefined ? t("files.share.publicLink.description") : tr(s.linkBlockedReason)}
              </Text>
            </Stack>
          </Stack>
          {s.link !== undefined ? (
            <Stack gap="2">
              <Stack direction="horizontal" gap="2" align="center" padding="2" radius="md" background="bg.surface" border="border.subtle">
                <Stack grow>
                  <Text mono selectable variant="footnote" lines={1} testID="file-share-public-url">
                    {s.link.url}
                  </Text>
                </Stack>
                <Button
                  label={t("files.share.publicLink.copy")}
                  icon="copy"
                  size="sm"
                  onPress={() => p.onCopy(s.link!.url)}
                  testID="file-share-public-copy"
                />
              </Stack>
              {s.link.expiresAt === undefined ? null : (
                <Text variant="caption" color="text.tertiary">
                  {t("sharing.expiry.on", { date: fmt.dateTime(s.link.expiresAt) })}
                </Text>
              )}
              {s.canManageLink ? (
                <Stack direction="horizontal" justify="end">
                  <Button label={t("files.share.publicLink.revoke")} variant="destructive" size="sm" loading={s.linkBusy} onPress={() => void vm.revokeLink()} testID="file-share-revoke" />
                </Stack>
              ) : null}
            </Stack>
          ) : s.canManageLink ? (
            <Stack direction="horizontal" align="center" gap="2">
              <Menu
                items={s.expiryOptions.map((o) => ({ id: `expiry-${o.key}`, label: tr(o.label), ...(o.key === s.linkExpiry ? { icon: "check" as const } : {}), onSelect: () => vm.setLinkExpiry(o.key) }))}
                align="start"
                testID="file-share-expiry-menu"
              >
                <Button variant="ghost" size="sm" label={tr(expiry.label)} onPress={() => {}} testID="file-share-expiry" />
              </Menu>
              <Stack grow />
              <Button label={t("files.share.publicLink.create")} icon="link" size="sm" loading={s.linkBusy} onPress={() => void vm.createLink()} testID="file-share-create-link" />
            </Stack>
          ) : null}
        </Stack>

        <Divider />

        {/* Who can access */}
        <Stack gap="2" testID="file-share-access">
          <Text variant="footnote" weight="semibold" color="text.secondary">
            {t("files.share.access")}
          </Text>
          {p.access.map((line) => (
            <Stack key={`${line.key}:${JSON.stringify(line.params)}`} direction="horizontal" align="start" gap="2">
              <Icon name="users" size="icon.sm" color="text.tertiary" />
              <Stack grow>
                <Text variant="footnote">{tr(line)}</Text>
              </Stack>
            </Stack>
          ))}
        </Stack>

        <Divider />
        <Stack direction="horizontal" align="center" justify="between">
          <Button
            label={t("sharing.copyLink")}
            icon="link"
            variant="secondary"
            onPress={() => p.onCopy(p.internalLink)}
            testID="file-share-copy-link"
          />
          <Button label={t("common.close")} onPress={p.onClose} testID="file-share-done" />
        </Stack>
      </Stack>
      <Toast
        open={s.error !== undefined || s.notice !== undefined}
        onClose={() => vm.dismissNotice()}
        message={s.error !== undefined ? tr(s.error) : s.notice !== undefined ? tr(s.notice) : ""}
        closeLabel={t("common.close")}
        durationMs={5000}
        testID="file-share-toast"
      />
    </Sheet>
  );
}
